// Calendar data helpers shared by the Calendar page, the country panel's
// "Coming up" section and the Today panel. Times arrive as UTC (`at`) and are
// shown in the visitor's own time zone by the browser (Intl).

import { t, lang } from '../i18n/index.js';

export const TYPES = ['central_bank', 'data_release', 'market_closed', 'early_close', 'public_holiday'];
export const EURO_AREA = new Set(['AT', 'BE', 'BG', 'CY', 'DE', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PT', 'SI', 'SK']);

const loc = lang === 'en' ? 'en-GB' : lang;
const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
const timeFmt = new Intl.DateTimeFormat(loc, { hour: '2-digit', minute: '2-digit' });
const dayFmt = new Intl.DateTimeFormat(loc, { weekday: 'long', day: 'numeric', month: 'long' });
const shortDayFmt = new Intl.DateTimeFormat(loc, { weekday: 'short', day: 'numeric', month: 'short' });

export const esc = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

export function safeHref(u) {
    try {
        const url = new URL(u);
        return url.protocol === 'https:' || url.protocol === 'http:' ? esc(url.href) : null;
    } catch {
        return null;
    }
}

export const flag = (iso2) =>
    iso2 === 'EU' ? '🇪🇺' : /^[A-Z]{2}$/.test(iso2 || '') ? String.fromCodePoint(...[...iso2].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)) : '🌍';

/** YYYY-MM-DD of a Date in the visitor's time zone. */
export function localDay(d = new Date()) {
    const p = Object.fromEntries(
        new Intl.DateTimeFormat('en-GB', { timeZone: userTz, year: 'numeric', month: '2-digit', day: '2-digit' })
            .formatToParts(d)
            .map((x) => [x.type, x.value]),
    );
    return `${p.year}-${p.month}-${p.day}`;
}

export function addDays(day, n) {
    const [y, m, d] = day.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** The visitor's day an event belongs to: timed events by local time, all-day by their own date. */
export const eventDay = (e) => (e.at ? localDay(new Date(e.at)) : e.date);

export function dayLabel(day, today = localDay()) {
    if (day === today) return t('cal.todayLabel');
    if (day === addDays(today, 1)) return t('cal.tomorrow');
    const [y, m, d] = day.split('-').map(Number);
    return dayFmt.format(new Date(Date.UTC(y, m - 1, d, 12)));
}

export function shortDay(day) {
    const [y, m, d] = day.split('-').map(Number);
    return shortDayFmt.format(new Date(Date.UTC(y, m - 1, d, 12)));
}

/** "14:00" in the visitor's time zone, or the all-day label. */
export function timeLabel(e) {
    if (!e.at) return t('cal.allDay');
    return timeFmt.format(new Date(e.at));
}

/** "in 2 d 4 h" / "in 35 min" / "now". */
export function countdown(at, now = Date.now()) {
    let s = Math.round((Date.parse(at) - now) / 1000);
    if (s <= 0) return t('cal.now');
    const d = Math.floor(s / 86400);
    s -= d * 86400;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s - h * 3600) / 60);
    return d > 0 ? t('cal.inDH', { d, h }) : h > 0 ? t('cal.inHM', { h, m }) : t('cal.inM', { m: Math.max(1, m) });
}

/** Translated, human title of an event. */
export function titleOf(e) {
    if (e.type === 'central_bank') return t('cal.decision', { bank: t(`bank.${e.bank}`) === `bank.${e.bank}` ? e.title.split(':')[0] : t(`bank.${e.bank}`) });
    if (e.type === 'data_release') {
        const k = `cal.series.${e.series}`;
        return t(k) === k ? e.title : t(k);
    }
    if (e.type === 'market_closed') return t('cal.closed', { exchange: e.exchangeName });
    if (e.type === 'early_close') return t('cal.early', { exchange: e.exchangeName });
    return e.i18n?.[lang] || e.title; // public holiday: local name when the library has it
}

/** Group events by the visitor's day: [[day, events], ...] in date order. */
export function groupByDay(events) {
    const map = new Map();
    for (const e of events) {
        const d = eventDay(e);
        if (!map.has(d)) map.set(d, []);
        map.get(d).push(e);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}

export async function fetchEvents({ from, to, countries = [], types = [] } = {}) {
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    if (countries.length) p.set('countries', countries.join(','));
    if (types.length) p.set('types', types.join(','));
    const res = await fetch(`/api/calendar?${p}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()).data;
}

export const icsUrl = (id) => `/api/calendar/event/${encodeURIComponent(id)}.ics`;

/** Absolute feed URL for the current filters (for "Subscribe"). */
export function feedUrl({ countries = [], types = [] } = {}) {
    const p = new URLSearchParams();
    if (countries.length) p.set('countries', countries.join(','));
    if (types.length) p.set('types', types.join(','));
    const q = p.toString();
    return `${location.origin}/api/calendar.ics${q ? `?${q}` : ''}`;
}
